/**
 * ポリシー設定 — 有効/無効の切り替えと、Keycloak 側の状態。
 *
 * 有効にすると realm `itmgmt` の**ブラウザフローが `itmgmt-browser` に変わる**
 * (ログインの途中でスイートに判定を聞くようになる)。戻すときはここを無効にすると
 * `browser` に戻る。ここが開けない状況で戻したいときは、Keycloak の管理コンソールで
 * realm の Browser flow を `browser` に選び直す(フロー自体は残しておく)。
 *
 * 判定 API が答えられないとき(api が落ちている・共有秘密が食い違う)、Keycloak 側は
 * **拒否に倒れる**(fail closed)。だから有効にする前に「共有秘密」と「SPI」の両方が
 * この画面で緑になっていることを確かめる。
 */
import { ReloadOutlined } from "@ant-design/icons";
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Flex,
  Form,
  Input,
  Modal,
  Space,
  Switch,
  Tag,
  Typography,
} from "antd";
import { useState } from "react";

import type { PolicyDecision } from "../../lib/api";
import { errorText, useIdpPerms } from "./hooks";
import {
  usePolicyGroups,
  usePolicyPreview,
  usePolicyStatus,
  useRecheckPolicyFlow,
  useSavePolicySettings,
} from "./policyHooks";
import { DECISION_ALLOW, DECISION_DENY, decisionColor } from "./policyOptions";

const { Text, Paragraph } = Typography;

function YesNo({ ok, yes, no }: { ok: boolean; yes: string; no: string }) {
  return <Tag color={ok ? "green" : "red"}>{ok ? yes : no}</Tag>;
}

/** 試算。**記録しない**ので、有効にする前の確認に使える。 */
function PreviewCard() {
  const preview = usePolicyPreview();
  const [out, setOut] = useState<PolicyDecision | null>(null);
  const [form] = Form.useForm<{
    userId: string;
    username?: string;
    clientId?: string;
    ip?: string;
    cert?: boolean;
  }>();

  return (
    <Card size="small" title="試算(この人がこの IP から来たらどうなるか)">
      <Paragraph type="secondary">
        判定だけして<Text strong>記録はしない</Text>。ユーザー id はユーザー一覧の
        詳細に出ている id を貼る。
      </Paragraph>
      <Form
        form={form}
        layout="inline"
        onFinish={(v) =>
          preview.mutate(
            {
              userId: v.userId,
              username: v.username ?? "",
              // 空の clientId は「登録されていないアプリケーション」で拒否に
              // なるので、既定はポータル自身の client を入れる
              clientId: v.clientId || "itmgmt-web",
              ip: v.ip ?? "",
              cert: !!v.cert,
            },
            { onSuccess: setOut },
          )
        }
      >
        <Form.Item name="userId" rules={[{ required: true, message: "ユーザー id は必須" }]}>
          <Input placeholder="ユーザー id(UUID)" style={{ width: 320 }} />
        </Form.Item>
        <Form.Item name="username">
          <Input placeholder="ユーザー名(任意)" style={{ width: 160 }} />
        </Form.Item>
        <Form.Item name="clientId" initialValue="itmgmt-web">
          <Input placeholder="ログイン先(clientId)" style={{ width: 200 }} />
        </Form.Item>
        <Form.Item name="ip">
          <Input placeholder="接続元 IP" style={{ width: 160 }} />
        </Form.Item>
        <Form.Item name="cert" label="証明書あり" valuePropName="checked">
          <Switch size="small" />
        </Form.Item>
        <Form.Item>
          <Button type="primary" htmlType="submit" loading={preview.isPending}>
            試算する
          </Button>
        </Form.Item>
      </Form>
      {out && (
        <Descriptions size="small" column={1} bordered style={{ marginTop: 12 }}>
          <Descriptions.Item label="判定">
            <Tag color={decisionColor(out.decision)}>{out.decision}</Tag>
            {out.decision !== DECISION_ALLOW && out.otpSetup && <Tag>登録させる</Tag>}
          </Descriptions.Item>
          <Descriptions.Item label="理由">{out.reason || "—"}</Descriptions.Item>
          <Descriptions.Item label="効いたポリシー">{out.group || "—"}</Descriptions.Item>
        </Descriptions>
      )}
    </Card>
  );
}

export function PolicySettingsPage() {
  const { canAdmin } = useIdpPerms();
  const status = usePolicyStatus();
  const groups = usePolicyGroups();
  const save = useSavePolicySettings();
  const recheck = useRecheckPolicyFlow();
  const s = status.data;
  const kc = s?.keycloak;
  // サーバーが「効かせられない理由」を返す。空でなければ切り替えさせない
  // (API 側も 400 で断るので、画面だけの制限ではない)
  const blockers = s?.enforceBlockers ?? [];
  const ready = blockers.length === 0;
  // **「反映待ち」は失敗ではない**(R7d fix2 の低 8)。別の反映がロックを持って
  // いるあいだは保存だけ通り(202)、Keycloak への反映は相手か 5 分ジョブが引き取る。
  // `applied` が unknown のままなのは「まだ確かめていない」であって壊れてはいない
  const waiting =
    String(save.data?.keycloak?.skipped ?? "") === "running" && s?.applied === "unknown";
  const defaultName =
    s?.defaultGroupName ||
    (groups.data?.items ?? []).find((g) => g.isDefault)?.displayName ||
    "";

  /** 有効化は**本番のログイン経路が変わる**ので、必ず確認を通す。 */
  const toggle = (next: boolean) => {
    Modal.confirm({
      title: next
        ? "本番の Keycloak のブラウザフローを切り替える"
        : "アクセスポリシーを無効に戻す",
      content: next ? (
        <Space direction="vertical">
          <span>
            realm の Browser flow が <Text code>itmgmt-browser</Text> に変わり、
            これ以降のログインは毎回スイートに判定を聞く。
          </span>
          <span>
            判定 API が答えられないときは<Text strong>ログインを拒否する</Text>
            (fail closed)。
          </span>
          {defaultName ? (
            <span>
              割当の無い人には既定のポリシー「{defaultName}」が効く。
            </span>
          ) : null}
        </Space>
      ) : (
        <span>
          Browser flow を <Text code>browser</Text> に戻す
          (<Text code>itmgmt-browser</Text> は消さずに残すので、また有効にできる)。
        </span>
      ),
      okText: next ? "有効にする" : "無効にする",
      okButtonProps: { danger: next },
      cancelText: "やめる",
      width: 560,
      onOk: () => save.mutate(next),
    });
  };

  return (
    <div>
      <Flex justify="space-between" align="center" wrap gap={8} style={{ marginBottom: 12 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          ポリシー設定
        </Typography.Title>
        <Space>
          <Button
            icon={<ReloadOutlined />}
            loading={recheck.isPending}
            onClick={() => recheck.mutate(undefined)}
            disabled={!canAdmin}
          >
            Keycloak を確認
          </Button>
          <Space>
            <span>アクセスポリシー</span>
            <Switch
              checked={!!s?.enforce}
              loading={save.isPending || status.isFetching}
              // 条件が揃っていないときは**入れられない**(戻すのはいつでもできる)
              disabled={!canAdmin || (!s?.enforce && !ready)}
              checkedChildren="有効"
              unCheckedChildren="無効"
              onChange={toggle}
            />
          </Space>
        </Space>
      </Flex>

      {status.isError && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="ポリシーの状態を取得できなかった"
          description={errorText(status.error)}
        />
      )}

      {s && !s.enforce && blockers.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="いまは「有効」にできない"
          description={
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {blockers.map((b) => (
                <li key={b}>{b}</li>
              ))}
            </ul>
          }
        />
      )}
      {s && !s.tokenSet && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="判定 API の共有秘密(POLICY_EVALUATE_TOKEN)が api に入っていない"
          description="この状態では Keycloak からの判定が 401 になり、有効にすると全員が拒否される。api と Keycloak の両方に同じ値を入れて、api を再起動する。"
        />
      )}
      {s && kc?.known && !kc.spiAvailable && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="Keycloak に判定用の Authenticator(itmgmt-policy)が入っていない"
          description="Keycloak のイメージを作り直して起動すると入る(providers は起動時に読まれる)。入っていない間はフローを切り替えない。"
        />
      )}
      {s && !s.enforce && kc?.known && kc.active && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="設定は無効なのに、Keycloak のブラウザフローが itmgmt-browser のまま"
          description="判定 API は設定が無効なら必ず許可を返すのでログインは通るが、フローは戻し切れていない。「Keycloak を確認」で戻す(5 分ごとのジョブでも戻る)。"
        />
      )}
      {s && kc?.known && kc.flowExists && kc.shapeOk === false && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="itmgmt-browser の形が今の期待と違う(古い形か、手で変えられている)"
          description="使用中のあいだは組み直さない(締め出しを作らないため)。無効にしてから「Keycloak を確認」を押すと、使われていないので組み直す。"
        />
      )}
      {s && s.applied === "unknown" && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="Keycloak のブラウザフローを確かめられていない"
          description="有効化・無効化の途中で Keycloak に届かなかった。「Keycloak を確認」で確かめ直す(この状態では alembic downgrade も止まる)。"
        />
      )}
      {s?.enforce && kc?.known && kc.brokeredOk === false && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="外部 IdP から戻るログインに判定が入っていない"
          description={`Keycloak は外部 IdP の認証後に元のブラウザフローを続けないので、この経路は判定を通らない(素通りになる)。外部 IdP ${kc.idpCount ?? 0} 件のうち ${kc.idpBound ?? 0} 件に itmgmt-post-login が入っている。「Keycloak を確認」で入れ直す。`}
        />
      )}
      {kc?.known && ((kc.foreignPostIdps?.length ?? 0) > 0 || !!kc.foreignBrowserFlow) && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="Keycloak に別の認証フローが入っている"
          description={`${kc.foreignBrowserFlow ? `realm のブラウザフローが ${kc.foreignBrowserFlow}。` : ""}${(kc.foreignPostIdps?.length ?? 0) > 0 ? `外部 IdP ${kc.foreignPostIdps!.join(" / ")} に別の Post Login Flow が入っている。` : ""}その realm の追加認証や利用規約を消さないよう、こちらからは触らない(外してから有効化する)。`}
        />
      )}
      {kc?.known && (kc.directGrantClients?.length ?? 0) > 0 && (
        <Alert
          type={s?.enforce ? "error" : "warning"}
          showIcon
          style={{ marginBottom: 12 }}
          message={
            s?.enforce
              ? "判定を迂回できる入口がある(direct access grants)"
              : "判定を通らない入口が残っている(direct access grants)"
          }
          description={`${kc.directGrantClients!.join(" / ")} は token endpoint に利用者名とパスワードを直接渡してトークンを取れる(認証フローを通らないので、接続元・サービス・追加認証の判定を迂回できる)。Keycloak の各 client で「Direct access grants」を切る。1 つでも残っているあいだは「有効」にできず、効かせたあとに増えた場合は「適用を確かめた結果」が unknown のままになる(判定そのものは効いたままにする)。`}
        />
      )}
      {kc?.known && kc.directGrantComplete === false && (
        <Alert
          type={s?.enforce ? "error" : "warning"}
          showIcon
          style={{ marginBottom: 12 }}
          message="判定を迂回できる入口を数え切れていない"
          description="client が多く、一覧を最後まで読めなかった。上に出ていない client にも direct access grants が残っている可能性がある(「無し」ではない)。数え切れるまで「有効」にはできない。"
        />
      )}
      {kc?.known && (kc.resetPasswordAllowed || kc.registrationAllowed) && (
        <Alert
          type={s?.enforce ? "error" : "warning"}
          showIcon
          style={{ marginBottom: 12 }}
          message="realm に判定を迂回できる入口がある(ログイン画面の外の導線)"
          description={`${kc.resetPasswordAllowed ? "「パスワードを忘れた」(resetPasswordAllowed)が有効: 判定を通らずに認証情報を変えられる。" : ""}${kc.registrationAllowed ? "「ユーザー登録」(registrationAllowed)が有効: 判定を通らずにアカウントを作れる。" : ""}Keycloak の realm 設定(Login)で切る。残っているあいだは「有効」にできない。`}
        />
      )}
      {waiting && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="反映待ち(別の反映が動いている)"
          description="設定は保存した。いま Keycloak を触っている側が終わると、この設定で反映される(遅くとも 5 分ごとのジョブで追いつく)。「適用を確かめた結果」が unknown のあいだは、まだ確かめられていないという意味。"
        />
      )}
      {s?.enforce && kc?.known && !kc.active && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="設定は有効だが、Keycloak のブラウザフローはまだ切り替わっていない"
          description="「Keycloak を確認」で反映し直せる(5 分ごとのジョブでも直る)。"
        />
      )}

      <Card size="small" style={{ marginBottom: 12 }}>
        <Descriptions size="small" column={2} bordered>
          <Descriptions.Item label="判定の適用(要求)">
            <YesNo ok={!!s?.enforce} yes="有効" no="無効(設定だけ)" />
          </Descriptions.Item>
          <Descriptions.Item label="適用を確かめた結果">
            {s?.applied ? (
              <Text code>{s.applied}</Text>
            ) : (
              <Text type="secondary">まだ確かめていない</Text>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="共有秘密">
            <YesNo ok={!!s?.tokenSet} yes="設定済み" no="未設定" />
          </Descriptions.Item>
          <Descriptions.Item label="Keycloak の Authenticator">
            {kc?.known ? (
              <YesNo ok={!!kc.spiAvailable} yes="入っている" no="入っていない" />
            ) : (
              <Tag>読めない{kc?.reason ? `(${kc.reason})` : ""}</Tag>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="ブラウザフロー">
            <Space>
              <Text code>{kc?.browserFlow || "—"}</Text>
              {kc?.flowExists && <Tag color="blue">itmgmt-browser あり</Tag>}
            </Space>
          </Descriptions.Item>
          <Descriptions.Item label="既定のポリシー">
            {defaultName || <Text type="secondary">無し(割当の無い人は通る)</Text>}
          </Descriptions.Item>
          <Descriptions.Item label="ポリシー / IP グループ">
            {s?.groupCount ?? "—"} / {s?.ipGroupCount ?? "—"}
          </Descriptions.Item>
          <Descriptions.Item label="割当の人数">{s?.assignedCount ?? "—"}</Descriptions.Item>
          <Descriptions.Item label="記録された判定">
            拒否 {s?.decisionCounts?.[DECISION_DENY] ?? 0} 件 / 追加認証{" "}
            {s?.decisionCounts?.require_otp ?? 0} 件
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {canAdmin && <PreviewCard />}
    </div>
  );
}
